import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { forEach, get, some } from 'lodash'
import Layout from 'containers/Layout'
import PageContent from 'components/templates/PageContent'
import LoadingIcon from 'components/atoms/LoadingIcon'
import apiGet from 'lib/network/apiGet'
import CompanyEcommerceTab from 'pages/company/CompanyEcommerceTab'
import { useTranslation } from 'react-i18next'

const CompanyEcommerceStandalonePage = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const user = useSelector(state => get(state, 'user.user'))
  const companyId = user?.company?._id || user?.company
  const isCompanyRole = some(user?.roles, { type: 'company' })
  const canBranding = Boolean(user?.canManageBranding)
  const canDns = Boolean(user?.canManageDns)
  const canEcommerce = canBranding || canDns

  const [companyData, setCompanyData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchCompany = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const response = await apiGet(`company/${companyId}`)
      const data = response?.data
      if (data) {
        forEach(Object.entries(data), ([key, value]) => {
          if (value === 'null') delete data[key]
        })
        setCompanyData(data)
      } else {
        setCompanyData(null)
      }
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    fetchCompany()
  }, [fetchCompany])

  const isDnsProvisioningLocked = useMemo(() => companyData?.dnsStatus === 'pending' || companyData?.dnsStatus === 'provisioning', [companyData?.dnsStatus])
  const initialSection = new URLSearchParams(location.search).get('section')

  if (!isCompanyRole) {
    return <Redirect to="/dashboard" />
  }
  if (!companyId) {
    return <Redirect to="/company-dashboard" />
  }
  if (!canEcommerce) {
    return <Redirect to="/company-dashboard" />
  }

  const title = t('sidebar.companyEcommerce')
  const pathname = '/company/ecommerce'

  return (
    <Layout
      title={title}
      breadcrumbs={[
        { name: t('sidebar.companyDashboard'), pathname: '/company-dashboard' },
        { name: title, pathname, current: true },
      ]}
      headerRight={
        <div className="mt-4 flex sm:mt-0 sm:ml-4">
          <button
            type="button"
            onClick={() => history.push('/company-dashboard')}
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-1 sm:ml-3"
          >
            {t('addEditPartner.buttons.goBack')}
          </button>
        </div>
      }
    >
      <PageContent>
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingIcon />
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[min(100%,100rem)] p-4 sm:p-6 lg:px-10">
            <CompanyEcommerceTab
              companyId={companyId}
              companyData={companyData}
              refetch={fetchCompany}
              dnsEditLocked={isDnsProvisioningLocked}
              canBranding={canBranding}
              canDns={canDns}
              initialSection={initialSection}
            />
          </div>
        )}
      </PageContent>
    </Layout>
  )
}

export default CompanyEcommerceStandalonePage
